<template>
    <div @focusout="onFocusOut" ref="mainContainer" class="site-header__center" :class="{ 'site-header__center--visible': searchIsOpen }">
        <div ref="container" class="search">
            <div class="search__overlay" @click="searchClear" :class="{ 'search__overlay--show': searchIsOpen }"></div>
            <form
                id="js-search-form"
                :action="dictionary.url.search"
                role="search"
                class="search__form"
                :class="{ 'search__form--focus': searchIsOpen }"
                data-cy="search"
            >
                <div class="d-flex w-100">
                    <div class="col d-flex position-relative px-0">
                        <input
                            id="js-search-form-q"
                            ref="searchInput"
                            class="search__input"
                            type="search"
                            :placeholder="dictionary.siteHeader.search.placeholder"
                            value=""
                            autocomplete="off"
                            name="q"
                            @keyup="searchKeyUp($event)"
                            @keyup.esc="whispererIsOpen = false"
                            @click="focusInput"
                            @focus="whispererIsOpen = true; focusInput()"
                        />
                        <span v-if="searchTerm.length > 0 && searchTerm !== ' '" class="search__clear" @click.prevent="clearSearchTerm">
                            <svg width="15" height="15" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="6" cy="6" r="6" fill="#B7B7B7" />
                                <path
                                    d="M4.04131 7.95869C4.06885 7.98623 4.10327 8 4.14114 8C4.179 8 4.21343 7.98623 4.24096 7.95869L6 6.19966L7.75904 7.95869C7.78658 7.98623 7.821 8 7.85886 8C7.89329 8 7.93115 7.98623 7.95869 7.95869C8.01377 7.90361 8.01377 7.81411 7.95869 7.75904L6.19966 6L7.95869 4.24096C8.01377 4.18589 8.01377 4.09639 7.95869 4.04131C7.90361 3.98623 7.81411 3.98623 7.75904 4.04131L6 5.80034L4.24096 4.04131C4.18589 3.98623 4.09639 3.98623 4.04131 4.04131C3.98623 4.09639 3.98623 4.18589 4.04131 4.24096L5.80034 6L4.04131 7.75904C3.98623 7.81411 3.98623 7.90361 4.04131 7.95869V7.95869Z"
                                    fill="white"
                                />
                            </svg>
                        </span>
                        <button class="search__submit">
                            <span>{{ dictionary.siteHeader.search.label }}</span>
                        </button>
                    </div>
                </div>
                <div
                    class="search-history__item search-history__item--full text-end"
                    :class="{ 'search-history__item--open': searchIsOpen }"
                >
                    <span class="text-dark cursor-point search__close" @click="searchClear">
                        {{ dictionary.close }}<i class="ico ico-close active"></i>
                    </span>
                </div>
            </form>

            <div class="search__container" :class="{ 'search__container--open': searchIsOpen }">
                <div class="d-md-none">
                    <div v-if="searchTerm.trim().length > 0 && emptyResponse" class="search__notfound header">
                        {{ dictionary.notFound }}
                    </div>
                    <div v-if="queries.length > 0" class="search__header d-md-none">
                        {{ dictionary.searchTern }}
                    </div>
                    <div v-if="queries.length > 0" class="search__terms d-md-none">
                        <div class="search__terms__wrap">
                            <div v-for="(item, i) in queries" :key="i" class="search__query">
                                <a
                                    :href="`${dictionary.url.search}?q=${item.query}`"
                                    class="search__item__title"
                                    v-html="item.htmlTitle"
                                ></a>
                            </div>
                        </div>
                    </div>

                    <div class="tabs__header d-sm-none">
                        <ul class="tabsList">
                            <li class="tabsList__item" v-if="products.length > 0">
                                <a
                                    href="#"
                                    class="tabsList__link"
                                    :class="{ 'tabsList__link--active': setTabActive === 1 }"
                                    @click.prevent="setTabActive = 1"
                                >
                                    {{ dictionary.products }}
                                </a>
                            </li>
                            <li class="tabsList__item" v-for="(cat, catName, i) in groupedCatalogues" :key="i">
                                <a
                                    href="#"
                                    class="tabsList__link"
                                    :class="{ 'tabsList__link--active': setTabActive === i + 2 }"
                                    @click.prevent="setTabActive = i + 2"
                                >
                                    {{ catName }}
                                </a>
                            </li>
                            <li class="tabsList__item" v-if="staticContents.length > 0">
                                <a
                                    href="#"
                                    class="tabsList__link"
                                    :class="{ 'tabsList__link--active': setTabActive === 99 }"
                                    @click.prevent="setTabActive = 99"
                                >
                                    {{ dictionary.siteHeader.search.staticContents }}
                                </a>
                            </li>
                            <li class="tabsList__item" v-if="pharmacies.length > 0">
                                <a
                                    href="#"
                                    class="tabsList__link"
                                    :class="{ 'tabsList__link--active': setTabActive === 100 }"
                                    @click.prevent="setTabActive = 100"
                                >
                                    {{ dictionary.siteHeader.search.pharmacies }}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div v-if="products.length < 0 && queries.length < 0 && !lastVisitsProductsNotEmpty" class="search__notfound">
                    <div v-if="inputIsNotEmpty">
                        <div class="title h3 my-sm-3 text-center mt-3 px-3">
                            {{ dictionary.search.notfoundTitle }}
                        </div>
                        <div class="desc text-center px-3 pb-2">
                            {{ dictionary.siteHeader.search.notfoundDesc }}
                        </div>
                    </div>
                    <div v-else>
                        <div class="desc text-center px-3 pb-2">
                            {{ dictionary.siteHeader.search.startSearch }}
                        </div>
                    </div>

                    <div v-if="lastVisitsProductsNotEmpty" class="search__notfound__lastVisit">
                        <div class="search__header">{{ dictionary.siteHeader.search.lastVisitsProducts }}</div>
                        <div v-for="(product, indexProducts) in lastVisitsProducts" :key="indexProducts" class="search__product">
                            <div class="search__product__image">
                                <!-- Link -->
                                <NuxtLink v-if="isPwa" to="/" role="link" tabindex="-1">
                                    <img
                                        v-if="product.image"
                                        v-lazy-load
                                        :src="`${product.image}`"
                                        :alt="product.name"
                                        @error="setAltImg"
                                    />
                                </NuxtLink>
                                <a v-else :href="product.url" role="link" tabindex="-1">
                                    <img
                                        v-if="product.image"
                                        v-lazy-load
                                        :src="`${product.image}`"
                                        :alt="product.name"
                                        @error="setAltImg"
                                    />
                                </a>
                                <!-- ende Link -->
                                <div v-if="product.isAvailable && product.percentDiscount && product.percentDiscount >= 3" class="discount">
                                    -{{ product.percentDiscount }}%
                                </div>
                            </div>
                            <div class="search__product__head">
                                <!-- Link -->
                                <NuxtLink
                                    class="search__product__title d-block"
                                    v-if="isPwa"
                                    to="/"
                                    role="link"
                                    tabindex="-1"
                                    v-html="product.name"
                                />
                                <a
                                    v-else
                                    class="search__product__title d-block"
                                    :href="product.url"
                                    role="link"
                                    tabindex="-1"
                                    v-html="product.name"
                                />
                                <!-- end Link -->
                                <div v-if="false && product.productRating.averageRating > 3" class="mb-1">
                                    <TheSearchRating
                                        :rating="product.productRating.averageRating"
                                        :total="product.productRating.totalReviews"
                                        :box="true"
                                    />
                                </div>
                            </div>
                            <div class="search__product__body">
                                <div class="info">
                                    <span
                                        v-if="product.availabilityPeriod"
                                        class="stock d-inline-block mb-1"
                                        :class="{ 'text-success': product.isAvailable, 'text-orange': !product.isAvailable }"
                                        v-html="product.fastestDeliveryDate"
                                    ></span>
                                    <div class="price">
                                        <!--                            :style="[ product.hasCompetitorSuperPrice || product.attributes.hasSuperPrice ? { 'margin-bottom': '-1em' } : '']"-->
                                        <!--                          <TheSearchSuperPrice-->
                                        <!--                            v-if="product.hasCompetitorSuperPrice && product.isAvailable"-->
                                        <!--                            :price-before="product.priceBeforeDiscount"-->
                                        <!--                            :price-actual="product.price"-->
                                        <!--                            :title-text="dictionary.competitorPrice.toUpperCase()"-->
                                        <!--                          />-->
                                        <!--                          <TheSearchSuperPrice-->
                                        <!--                            v-else-if="product.attributes.hasSuperPrice && product.isAvailable"-->
                                        <!--                            :price-before="product.priceBeforeDiscount"-->
                                        <!--                            :price-actual="product.price"-->
                                        <!--                            :title-text="dictionary.superPrice.toUpperCase()"-->
                                        <!--                          />-->
                                        <div v-if="product.isAvailable">
                                            <span
                                                v-if="product.priceBeforeDiscount !== null"
                                                class="mr-2 d-inline-block notranslate"
                                                v-html="$formatCurrency(product.priceBeforeDiscount)"
                                                style="text-decoration: line-through"
                                            ></span>
                                            &nbsp;<span
                                                class="font-weight-bold d-inline-block notranslate"
                                                v-html="$formatCurrency(product.price)"
                                            ></span>
                                        </div>
                                        <div
                                            v-if="product.attributes.comparablePrice"
                                            class="price-perunit"
                                            v-html="product.attributes.comparablePrice"
                                        ></div>
                                        <!--                            :style="[ product.hasCompetitorSuperPrice || product.attributes.hasSuperPrice ? { 'margin-top': '-0.75rem', 'margin-bottom': '0.75rem' } : '']"-->
                                    </div>
                                </div>
                                <AddBasket
                                    v-if="product.isAvailable && isPwa && (!product.isPremiumExclusive || isUserPremium)"
                                    :dictionary="dictionary"
                                    :product="product"
                                    @addProductToBasket="addProductToBasket"
                                    :basket-amount="productAmounts[product.id] || 0"
                                    item-list-name="Header_search_product"
                                />
                                <!-- <AddBasketOldBasket else :product="product" />-->
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="setTabActive === 1" class="search__right" @scroll="unFocusInput">
                    <template v-if="products.length > 0">
                        <div class="search__right__scroll">
                            <div v-if="queries.length > 0" class="search__header d-none d-md-block">
                                {{ dictionary.searchTern }}
                            </div>
                            <div v-if="queries.length > 0" class="search__terms d-none d-md-block">
                                <div v-for="(item, i) in queries" :key="i" class="search__query">
                                    <a
                                        :href="`${dictionary.url.search}?q=${item.query}`"
                                        class="search__item__title"
                                        v-html="item.htmlTitle"
                                    ></a>
                                </div>
                            </div>

                            <div class="search__header d-none d-md-block">{{ dictionary.products }}</div>

                            <div
                                v-for="(product, indexProducts) in products"
                                :key="indexProducts + last"
                                class="search__product"
                                :class="{ 'active-item': currentItem === indexProducts + last }"
                            >
                                <div class="search__product__image">
                                    <a :href="product.url" tabindex="-1">
                                        <img
                                            v-if="product.image"
                                            data-not-lazy
                                            :src="`${product.image}`"
                                            :alt="product.name"
                                            @error="setAltImg"
                                        />
                                    </a>
                                    <div
                                        v-if="product.isAvailable && product.percentDiscount && product.percentDiscount >= 3"
                                        class="discount"
                                    >
                                        -{{ product.percentDiscount }}%
                                    </div>
                                </div>
                                <div class="search__product__head">
                                    <a
                                        class="d-block search__product__title notranslate"
                                        :href="product.url"
                                        @click="emitAutocompleteClickResult('click', product.url)"
                                        v-html="product.value"
                                    ></a>
                                    <div v-if="false && product.productRating.averageRating > 3" class="mb-1">
                                        <TheSearchRating
                                            :rating="product.productRating.averageRating"
                                            :total="product.productRating.totalReviews"
                                            :box="true"
                                        />
                                    </div>
                                </div>
                                <div class="search__product__body">
                                    <div class="info">
                                        <span
                                            v-if="product.availabilityPeriod"
                                            class="stock d-inline-block mb-1"
                                            :class="{ 'text-success': product.isAvailable, 'text-orange': !product.isAvailable }"
                                            v-html="product.fastestDeliveryDate"
                                        ></span>
                                        <div class="price">
                                            <!--                            :style="[ product.hasCompetitorSuperPrice || product.attributes.hasSuperPrice ? { 'margin-bottom': '-1em' } : '']"-->
                                            <!--                          <TheSearchSuperPrice-->
                                            <!--                            v-if="product.hasCompetitorSuperPrice && product.isAvailable"-->
                                            <!--                            :price-before="product.priceBeforeDiscount"-->
                                            <!--                            :price-actual="product.price"-->
                                            <!--                            :title-text="dictionary.competitorPrice.toUpperCase()"-->
                                            <!--                          />-->
                                            <!--                          <TheSearchSuperPrice-->
                                            <!--                            v-else-if="product.attributes.hasSuperPrice && product.isAvailable"-->
                                            <!--                            :price-before="product.priceBeforeDiscount"-->
                                            <!--                            :price-actual="product.price"-->
                                            <!--                            :title-text="dictionary.superPrice.toUpperCase()"-->
                                            <!--                          />-->
                                            <div v-if="product.isAvailable">
                                                <span
                                                    v-if="product.priceBeforeDiscount !== null"
                                                    class="mr-2 d-inline-block notranslate"
                                                    v-html="$formatCurrency(product.priceBeforeDiscount)"
                                                    style="text-decoration: line-through"
                                                ></span>
                                                &nbsp;<span
                                                    class="font-weight-bold d-inline-block notranslate"
                                                    v-html="$formatCurrency(product.price)"
                                                ></span>
                                            </div>
                                            <div
                                                v-if="product.attributes.comparablePrice"
                                                class="price-perunit"
                                                v-html="product.attributes.comparablePrice"
                                            ></div>
                                            <!--                            :style="[ product.hasCompetitorSuperPrice || product.attributes.hasSuperPrice ? { 'margin-top': '-0.75rem', 'margin-bottom': '0.75rem' } : '']"-->
                                        </div>
                                    </div>
                                    <AddBasket
                                        v-if="product.isAvailable && isPwa && (!product.isPremiumExclusive || isUserPremium)"
                                        :dictionary="dictionary"
                                        :product="product"
                                        @addProductToBasket="addProductToBasket"
                                        :basket-amount="productAmounts[product.id] || 0"
                                        item-list-name="Header_search_product"
                                    />
                                </div>
                            </div>
                        </div>

                        <div
                            v-if="searchTerm.trim().length > 0 && products.length > 0 && !emptyResponse"
                            class="search__show_more_products"
                        >
                            <a :href="`${dictionary.url.search}?q=${searchTerm}`" class="btn btn-primary">
                                {{ dictionary.siteHeader.searchResults }}
                            </a>
                        </div>
                    </template>
                </div>

                <div class="search__left">
                    <div
                        :class="{ 'd-block': setTabActive === index + 2 }"
                        :data-index="`${index + 2}`"
                        v-for="(catalog, catalogName, index) in groupedCatalogues"
                        :key="index"
                    >
                        <div class="catalogues mb-3">
                            <div class="search__header d-none d-md-block">{{ catalogName }}</div>
                            <div
                                v-for="(item, i) in catalog"
                                :key="i"
                                class="search__item"
                                :class="{
                                    notranslate: [dictionary.searchBrands, dictionary.searchProductLines].includes(catalogName),
                                }"
                            >
                                <a :href="item.url" class="search__item__title" v-html="item.name"></a>
                            </div>
                        </div>
                    </div>
                    <div :class="{ 'd-block': setTabActive === 99 }" v-if="staticContents.length > 0">
                        <div class="catalogues mb-3">
                            <div class="search__header d-none d-md-block">
                                {{ dictionary.siteHeader.search.staticContents }}
                            </div>
                            <div v-for="(item, i) in staticContents" :key="i" class="search__item">
                                <a :href="item.url" class="search__item__title" v-html="item.name"></a>
                            </div>
                        </div>
                    </div>
                    <div :class="{ 'd-block': setTabActive === 100 }" v-if="pharmacies.length > 0">
                        <div class="catalogues mb-3">
                            <div class="search__header d-none d-md-block">
                                {{ dictionary.siteHeader.search.pharmacies }}
                            </div>
                            <div v-for="(item, i) in pharmacies" :key="i" class="search__item">
                                <a :href="item.url" class="search__item__title" v-html="item.name"></a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AddBasket from './AddBasket'
import TheSearchRating from './TheSearchRating'

export default {
    components: {
        TheSearchRating,
        AddBasket,
    },
    data() {
        return {
            searchTimeout: null,
            searchTerm: ' ',
            products: [],
            lastVisitsProducts: [],
            lastVisitsProductsNotEmpty: false,
            inputIsNotEmpty: false,
            services: [],
            pharmacies: [],
            serviceOffices: [],
            staticContents: [],
            queries: [],
            catalogues: [],
            productsCount: 0,
            currentItem: 0,
            brands: [],
            categories: [],
            imageAlt: {
                type: String,
                default: 'Product image',
                required: false,
            },
            setTabActive: 1,
            searching: false,
            emptyResponseData: {},
            emptyResponse: false,
        }
    },

    props: {
        searchIsOpen: {
            type: Boolean,
            required: true,
        },
        productAmounts: {
            type: Object,
            required: false,
            default: () => {},
        },
        baseUrl: {
            type: String,
            default: '',
        },
        isPwa: {
            type: Boolean,
            default: true,
        },
        lastVisitedProductIds: {
            type: Array,
            required: true,
        },
        dictionary: {
            type: Object,
            required: true,
        },
        isUserPremium: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        last() {
            return Object.keys(this.catalogues).length
        },
        groupedCatalogues() {
            return this.groupBy(this.catalogues, 'result_group_label')
        },
    },

    watch: {
        searchIsOpen() {
            document.documentElement.className = this.searchIsOpen ? 'search-open' : ''
        },
        isMobileVersionVisible() {
            this.$nextTick(() => this.$refs.searchInput.focus())
        },
    },

    mounted() {
        this.$refs.searchInput.value = new URLSearchParams(window.location.search).get('q') || ''

        document.addEventListener('keyup', ({ key }) => {
            if (key === 'ArrowDown') {
                this.nextItem()
            }
            if (key === 'ArrowUp') {
                this.previousItem()
            }
            if (key === 'Enter') {
                this.goToPage()
            }
        })

        window.addEventListener('load', function () {
            let i = 0
            const searchFormElement = document.getElementById('js-search-form-q')
            let txt = ''
            if (searchFormElement) {
                txt = searchFormElement.placeholder
                searchFormElement.placeholder = ''
            }

            function searchWriter() {
                if (i < txt.length) {
                    searchFormElement.placeholder += txt.charAt(i)
                    i++
                    setTimeout(searchWriter, 125)
                }
            }

            searchWriter()
        })

        this.search()
    },

    methods: {
        addProductToBasket(data) {
            this.$emit('addProductToBasket', data)
            this.emitAutocompleteClickResult('conversion', data.product?.url)
        },

        getCookie(cname) {
            let name = cname + '='
            let decodedCookie = decodeURIComponent(document.cookie)
            let ca = decodedCookie.split(';')
            for (let i = 0; i < ca.length; i++) {
                let c = ca[i]
                while (c.charAt(0) === ' ') {
                    c = c.substring(1)
                }
                if (c.indexOf(name) === 0) {
                    return c.substring(name.length, c.length)
                }
            }
            return ''
        },

        searchKeyUp($event) {
            this.whispererIsOpen = true
            this.$emit('opened')
            this.inputIsNotEmpty = true
            this.searchTerm = $event.target.value
            this.emptyResponse = false

            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout)
            }

            if (this.searchTerm.length < 3 && this.searchTerm.length !== 0) {
                return
            }

            if ($event.key !== 'Backspace') {
                this.products = []
                this.services = []
                this.pharmacies = []
                this.serviceOffices = []
                this.staticContents = []
                this.queries = []
                this.productsCount = 0
                this.catalogues = []
            }

            this.searchTimeout = setTimeout(this.search, 500)
        },

        async search() {
            this.searching = true
            const url = `${this.baseUrl}/backend/hledej-ajax?q=${this.searchTerm}`
            let searchData = {}
            if (this.isPwa) {
                // eslint-disable-next-line
                searchData = await $fetch(url)
            } else {
                searchData = await fetch(url, { method: 'get' })
                searchData = await searchData.json()
            }

            if (this.searchTerm.trim() === '') {
                this.emptyResponseData = searchData
            }

            if (
                searchData.products.length === 0 &&
                searchData.services.length === 0 &&
                searchData.pharmacies.length === 0 &&
                searchData.serviceOffices.length === 0 &&
                searchData.staticContents.length === 0 &&
                searchData.queries.length === 0 &&
                searchData.catalogues.length === 0
            ) {
                this.emptyResponse = true
                searchData = this.emptyResponseData
            }

            this.products = searchData.products
            this.services = searchData.services
            this.pharmacies = searchData.pharmacies
            this.serviceOffices = searchData.serviceOffices
            this.staticContents = searchData.staticContents
            this.queries = searchData.queries
            this.productsCount = searchData.total.product
            this.catalogues = searchData.catalogues

            if (this.products.length > 0) {
                this.setTabActive = 1
            } else if (this.catalogues.length > 0) {
                // since there are no products and catalog tabs are indexed from 2
                this.setTabActive = 2
            } else if (this.staticContents.length > 0) {
                this.setTabActive = 99
            } else if (this.pharmacies.length > 0) {
                this.setTabActive = 100
            }

            this.emitAutocompleteResults()

            this.searching = false
        },

        emitAutocompleteResults() {
            if (this.searchTerm === '') {
                return
            }

            const luigisBoxItems = []
            let position = 0
            this.products.forEach((product) => {
                position++
                luigisBoxItems.push({
                    title: product.name,
                    url: product.url,
                    position,
                    price: product.price,
                    type: 'item',
                })
            })

            Object.values(this.groupedCatalogues).forEach((catalog) => {
                catalog.forEach((catalogItem) => {
                    position++
                    luigisBoxItems.push({
                        title: catalogItem.name,
                        url: catalogItem.url,
                        position,
                        price: null,
                        type: catalogItem.type,
                    })
                })
            })

            this.staticContents.forEach((staticContent) => {
                position++
                luigisBoxItems.push({
                    title: staticContent.name,
                    url: staticContent.url,
                    position,
                    price: null,
                    type: staticContent.type,
                })
            })

            this.pharmacies.forEach((pharmacy) => {
                position++
                luigisBoxItems.push({
                    title: pharmacy.name,
                    url: pharmacy.url,
                    position,
                    price: null,
                    type: pharmacy.type,
                })
            })

            this.queries.forEach((query) => {
                position++
                luigisBoxItems.push({
                    title: query.query,
                    url: `${this.dictionary.url.search}?q=${query.query}`,
                    position,
                    price: null,
                    type: 'query',
                })
            })

            this.$emit('autocompleteResults', this.searchTerm, luigisBoxItems)
        },

        emitAutocompleteClickResult(clickType, productUrl) {
            this.$emit('autocompleteClickResult', clickType, productUrl)
        },

        groupBy(array, key) {
            const result = {}
            array.forEach((item) => {
                if (!result[item[key]]) {
                    result[item[key]] = []
                }
                result[item[key]].push(item)
            })
            return result
        },
        focusInput() {
            this.$refs.searchInput.focus()

            setTimeout(() => {
                this.$emit('opened')

                document.body.classList.add('noscroll')
            }, 300)
        },
        unFocusInput() {
            this.$refs.searchInput.blur()
        },
        searchClear() {
            document.body.classList.remove('noscroll')
            this.$emit('closed')
            this.searchTerm = ''
        },
        nextItem() {
            this.currentItem++
        },
        previousItem() {
            this.currentItem--
        },
        goToPage() {
            document.querySelector('.active-item')?.click()
        },
        clearSearchTerm() {
            this.$refs.searchInput.value = ''
            this.searchTerm = ''
        },

        setAltImg(event) {
            event.target.src = '/nuxt-assets/images/lazy-load-error.svg'
        },

        onFocusOut(event) {
            if (!this.$refs.mainContainer.contains(event.relatedTarget)) {
                // console.log('CLOSED')
                // this.$emit('closed')
            }
        },
    },
}
</script>

<style lang="scss">
$border-radius: 25px;
$form-padding: 10px;
$primary: #146154;
$grey-extra-light: #fbfbfb;
$grey-semi-dark: #b7b7b7;
$color-grey-extra-dark: #696969;

input[type='search']::-webkit-search-decoration,
input[type='search']::-webkit-search-cancel-button,
input[type='search']::-webkit-search-results-button,
input[type='search']::-webkit-search-results-decoration {
    -webkit-appearance: none;
}

#search-clear {
    display: none !important;
}

.search-open {
    @media (min-width: 1000px) {
        overflow: visible;
    }

    .menuFooterMobile {
        display: none;
    }
}

.search-wrapper {
    width: 100%;

    @media (min-width: 480px) {
        height: 57px;
    }

    @media (min-width: 1000px) {
        margin-right: 1em;
        position: relative;
        max-width: 708px;
    }
}

.search {
    display: grid;
    grid-template-rows: 60px 1fr;
    position: relative;
    flex-grow: 1;
    font-size: 14px;
    z-index: 201;
    max-width: 708px;
    height: 100vh;

    @media (min-width: 768px) {
        height: auto;
    }

    &__clear {
        display: none;
        position: absolute;
        right: 15px;
        top: 11px;
        font-size: 15px;

        @media (min-width: 980px) {
            right: 15%;
            top: 12px;
        }
    }

    &__top {
        padding: 0 0 1em;
        grid-column: 1 / span 2;
        width: 100%;
        max-width: calc(100vw - 3em);
        overflow-x: auto;
    }

    .search-tabs {
        list-style: none;
        padding-left: 0;
        margin-bottom: 0;
        display: flex;
        flex-wrap: nowrap;

        a {
            display: inline-block;
            border-radius: 10px;
            font-size: 14px;
            padding: 7px 15px;
            margin-right: 0.5em;
            text-align: center;
            color: #424242;
            font-weight: 600;
            white-space: nowrap;
        }

        .active a {
            background-color: #ffffff;
            color: #333333;
        }
    }

    &__overlay {
        visibility: hidden;
        position: fixed;
        z-index: 101;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: #000;
        opacity: 0.5;
        transition: visibility 0s, opacity 150ms ease-in-out;

        @media (min-width: 768px) {
            &--show {
                visibility: visible;
                transition: 150ms ease-in-out;
            }
        }
    }

    &--show {
        position: absolute;
        width: 708px;
        max-width: 708px;
        left: 0;

        @media (min-width: 768px) {
            top: 10px;
        }

        @media (min-width: 768px) and (max-width: 1200px) {
            position: absolute;
            right: 0;
            margin: auto;
        }

        @media (min-width: 1200px) {
            top: 0;
        }
    }

    &__close {
        font-size: 0;

        .ico {
            opacity: 0.5;

            &:hover {
                opacity: 1;
            }
        }
    }

    &__header {
        text-transform: uppercase;
        color: #333333;
        padding: 0.5em 0;

        @media (min-width: 768px) {
            padding: 0.5em 1em;
        }
    }

    .search__notfound {
        grid-column: 1 / span 2;
        padding-bottom: 1rem;

        &.header {
            text-align: center;
            padding: 1rem;
            font-weight: 600;
        }

        @media (min-width: 768px) {
            order: 1;
        }
    }

    .search__left {
        background-color: #ffffff;
        padding: 0 15px;

        @media (max-width: 768px) {
            //height: 100%;
            //display: none;

            > div {
                display: none;
            }
        }

        @media (min-width: 768px) {
            margin-bottom: 0px;
            order: 3;
            overflow-y: scroll;
            flex: 0 0 40%;
            height: 100%;
            max-height: 87vh;
        }
    }

    .search__right {
        width: calc(100% + 30px);
        padding: 0 0 15px;
        margin: 0 -15px;
        background-color: #ffffff;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        position: relative;

        @media (min-width: 768px) {
            order: 2;
            width: auto;
            padding: 0;
            margin: 0;
            background-color: #ffffff;
            max-height: 87vh;
        }

        &__scroll {
            overflow-y: scroll;
            flex: 1;
            padding-bottom: 55px;
        }
    }

    @media (max-width: 768px) {
        width: 100%;

        .search__input {
            width: 100%;
        }
        &--visible {
            position: relative;
        }
    }

    &--visible {
        display: block;
    }

    &-history__item {
        display: none;
        padding: 0.75em;
        padding-left: 1em;
        line-height: 0;

        @media (min-width: 768px) {
            position: absolute;
            bottom: -45px;
            right: 1em;
        }

        &--open {
            display: block;
        }
    }

    &__form {
        display: flex;
        justify-content: center;
        width: 100%;
        padding: $form-padding;

        @media (min-width: 768px) {
            button {
                margin-left: -25px;
            }
        }
    }

    &__input {
        width: 100%;
        padding: 0 25px 0 20px;
        line-height: 38px;
        border-radius: 25px;
        border: 1px solid #cbd5e1;
        position: relative;
        outline: 0;
        transition: 150ms ease-in-out;
        background: #f1f5f9; // url('/nuxt-assets/icons/search.svg') no-repeat;
        background-size: 18px 18px;
        background-position: 10px 10px !important;
        color: #334155;

        &::placeholder {
            color: #64748b;
        }

        -webkit-appearance: none;
        font-size: 12px;

        &::-webkit-clear-button,
        &::-ms-clear {
            display: none;
        }

        @media (min-width: 768px) {
            background-position: 10px 8px;
        }
    }

    &__submit {
        border-radius: 25px;
        padding: 0;
        height: 40px;
        width: 50px;
        line-height: 40px;
        text-align: center;
        //background: $primary;
        color: #fff;
        border: 0;
        z-index: 10;
        //width: 100px;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        position: absolute;
        right: 0;
        top: 0;
        text-indent: -9999px; /* posune text mimo viditelnou oblast */
        background: #064740 url('https://www.pilulka.cz/nuxt-assets/icons/search-white.svg') no-repeat center center;
        background-size: 18px 18px;

        @media (max-width: 768px) {
            display: none;
        }

        &:hover {
            @media (min-width: 992px) {
                //background-color: #064740;
                //color: #9ffa9d;
            }
        }
    }

    &__form--focus {
        position: relative;
        z-index: 103;
        border-radius: $border-radius;

        @media (min-width: 768px) {
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
            background-color: #fff;
        }
    }

    &__container {
        display: none;
        position: relative;
        background: #ffffff;
        padding: 0.5em 15px 0 15px;
        width: 100vw;
        z-index: 102;
        overflow: hidden;
        flex-grow: 1;
        margin: -1px 0 0 0;

        &--open {
            display: flex;
            flex-direction: column;
            grid-template-rows: auto 1fr;
            //min-height: 85vh;
        }

        @media (min-width: 768px) {
            padding: 0.5em 0 16px 0;
            margin: -1px 0 0 0;
            max-height: calc(100vh - 75px);
            grid-template-columns: 60% 40%;
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            border-radius: $border-radius;
            border-top-left-radius: 0;
            border-top-right-radius: 0;

            &--open {
                display: grid;
                width: 100%;
            }
        }
    }

    &__item {
        display: flex;
        width: 100%;
        justify-content: space-between;

        &:hover {
            text-decoration: none;
            background: $grey-extra-light;
        }

        &.active-item {
            background: #eff3ed;
        }

        &--header {
            color: #333333;
            font-weight: 600;
        }

        &__title {
            display: block;
            width: 100%;
            color: #333;
            padding: 6px 0;
            font-weight: 600;

            @media (min-width: 768px) {
                padding: 6px 15px;
            }

            em {
                background: #e9f8eb;
                color: #31a993;
                font-style: normal;
            }
        }

        &__type {
            color: $grey-semi-dark;
        }
    }

    &__terms {
        @media (max-width: 767px) {
            overflow-x: auto;
            white-space: nowrap;
            margin-left: -15px;
            padding: 0 15px;
            width: calc(100% + 30px);
        }

        &__wrap {
            @media (max-width: 767px) {
                display: inline-flex;
            }
        }

        @media (min-width: 768px) {
            margin-left: 15px;
        }
    }

    &__query {
        background: #f6f6f6;
        text-decoration: none;
        align-items: center;
        margin: 5px 10px 5px 0;
        width: auto;
        display: inline-flex;
        border-radius: 15px;

        .search__item__title {
            padding: 6px 15px;
        }

        &:hover {
            background: #f6f6f6;
        }
    }

    &__product {
        display: grid;
        grid-auto-flow: column;
        grid-template-columns: 80px;
        align-items: center;
        padding: 10px 15px;

        &__button {
            align-items: flex-end;
            display: flex;
            justify-content: right;
            margin-bottom: 5px;
            margin-left: 10px;

            @media (min-width: 768px) {
                max-width: 155px;
            }

            .add-cart {
                margin-top: 5px;
                display: flex;
                align-items: center;
            }
        }

        &.active-item {
            background: $grey-extra-light;
        }

        &__button .add-cart__simple {
            padding: 3px 15px;
            border-radius: $border-radius;

            @media (min-width: 768px) {
                max-width: 100%;
                width: 140px;
                white-space: nowrap;
            }
        }

        &__head {
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
        }

        &__body {
            justify-content: space-between;
            display: flex;

            .text-success.stock {
                color: #567746 !important;
            }

            @media (max-width: 410px) {
                min-width: 270px;
            }
        }

        &__rating {
            margin-left: auto;
            padding-top: 5px;

            .star {
                background-size: 12px 12px;
                width: 12px;
                height: 12px;

                .ico {
                    width: 12px;
                    height: 12px;
                }
            }
        }

        &__image {
            grid-row: 1 / 3;
            flex: 0 0 70px;
            padding-right: 20px;
            position: relative;
            justify-content: center;
            text-align: center;

            .discount {
                position: absolute;
                background: #ffca63;
                top: -10px;
                right: 10px;
                width: 36px;
                height: 36px;
                font-size: 10px;
                min-width: 36px;
            }

            img {
                max-width: 60px;
                max-height: 80px;
            }
        }

        &__title {
            flex-grow: 1;
            padding-right: 10%;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            margin-bottom: 0.5em;
            font-weight: 600;
            color: #333;

            a {
                color: #333333;
                text-decoration: none;

                &:hover {
                    color: #333333;
                    text-decoration: underline;
                }
            }

            em {
                background: #fddde2;
                color: #146154;
                font-style: normal;
            }
        }

        &__price {
            color: #333333;
            padding-right: 15px;
            white-space: nowrap;
            font-weight: 600;
        }
    }
}

.search__notfound__lastVisit {
    background-color: #ffffff;
    margin-right: -15px;
    margin-left: -15px;

    @media (min-width: 768px) {
        margin: 0;
    }

    .search__header {
        padding: 0.5em 15px;
    }
}

@media (max-width: 767px) {
    .search-open {
        .search__clear {
            display: block;
        }
    }
}

.price .font-weight-bold {
    color: #146154;
    font-weight: 700;
}

.price-perunit {
    color: $color-grey-extra-dark;
    font-size: 12px;
}

.tabs__header {
    overflow-x: auto;
    white-space: nowrap;
    width: calc(100% + 30px);
    margin: 1rem 0 0 -15px;
    padding: 0 15px 1rem 15px;
}

.tabsList {
    display: inline-flex;
    list-style: none;
    padding: 0;
    margin: 0;
}

.tabsList__item {
    overflow: hidden;

    &:active,
    &:focus {
        outline: none;
    }

    &:first-child {
        border-top-left-radius: 10px;
        border-bottom-left-radius: 10px;
    }

    &:last-child {
        border-top-right-radius: 10px;
        border-bottom-right-radius: 10px;
    }
}

.tabsList__link {
    display: inline-block;
    padding: 0 0.875rem;
    font-size: 0.875rem;
    line-height: 2.5rem;
    background-color: #f6f6f6;
    cursor: pointer;
    color: #333333;
    font-weight: 600;

    &:active,
    &:focus {
        outline: none;
    }

    &:hover {
        color: #333333;
        outline: none;

        @media (min-width: 1024px) {
            background-color: #e1e1e1;
        }
    }

    &--active {
        background-color: #fddde2;
    }
}

.christmas-link {
    color: #146154;
    background: #fddde2;
    border-radius: 25px;
    display: flex;
    align-items: center;
    font-weight: 500;
    padding: 0 10px 0 5px;
    height: 32px;
}

.search__show_more_products {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 0.5rem 1rem;
    background-color: #fff;
    text-align: center;
    z-index: 9999;

    @media (max-width: 768px) {
        box-shadow: 0px -4px 4px 0px #00000012;
        position: fixed;
    }

    //@media (min-width: 768px) {
    //    position: relative;
    //    background-color: transparent;
    //    order: 3;
    //}
}
</style>
